// src/stores/auth.js
import { defineStore } from 'pinia'
import { useGenOptStore } from '@/stores/useGenOptStore.js'
import { ref, computed } from 'vue'
import Cookies from 'js-cookie'
import { encryptData, decryptData } from '@/utils/crypto'
import { instance } from '@/plugins/axios.js'
import {
  TOKEN_COOKIE_NAME,
  TOKEN_EXPIRY_DATE_COOKIE_NAME,
  USER_INFO_COOKIE_NAME,
} from '@/config/base.constant.js'
import { requestBuilder } from '@/utils/request-builder.utilities.js'
import { processResponse } from '@/utils/process-response-utilities.js'
import { queryClient } from '@/plugins/query-client.js'

const { addRecord } = requestBuilder()
export const useAuthStore = defineStore('auth', () => {
  const genOptionStore = useGenOptStore()
  const token = ref(decryptData(Cookies.get(TOKEN_COOKIE_NAME)))
  const expiresAt = ref(decryptData(Cookies.get(TOKEN_EXPIRY_DATE_COOKIE_NAME)))
  const userInfo = ref(decryptData(Cookies.get(USER_INFO_COOKIE_NAME)))

  const isSecure = window.location.protocol === 'https:'
  const authApiUrl = import.meta.env.VITE_AUTH_API_URL
  const userApiURL = import.meta.env.VITE_USERS_API_URL


  const getUserToken = computed(() => token.value)
  const getTokenExpiresAt = computed(() => expiresAt.value)
  const getUserLogInfo = computed(() => userInfo.value)

  const isLoggedIn = computed(() => {
    return !!token.value && !!expiresAt.value && new Date(expiresAt.value) > new Date()
  })

  const getUserFullName = computed(() => {
    const user = userInfo.value
    if (!user) return 'Anonymous'

    const getMiddleInitial = (middleName) => {
      const initial = String(middleName || '').trim().charAt(0)
      return initial ? `${initial}.` : ''
    }
    const getSuffix = (suffix) => {
      const trimmedSuffix = String(suffix || '').trim()
      return trimmedSuffix ? `, ${trimmedSuffix}` : ''
    }

    const nameParts = [
      user.first_name,
      getMiddleInitial(user.middle_name),
      user.last_name,
    ].filter(Boolean)

    let fullName = nameParts.join(' ')
    fullName += getSuffix(user.suffix)
    return fullName.trim() || 'Anonymous'
  })


  function setAuthCookies(newToken, newExpiresAt, newUserInfo) {
    const cookieOptions = {
      expires: new Date(newExpiresAt),
      secure: isSecure,
      httpOnly: false,
      sameSite: 'strict',
    }
    token.value = newToken
    expiresAt.value = newExpiresAt
    userInfo.value = newUserInfo
    if (newToken) {
      Cookies.set(TOKEN_COOKIE_NAME, encryptData(newToken), cookieOptions)
      Cookies.set(TOKEN_EXPIRY_DATE_COOKIE_NAME, encryptData(newExpiresAt), cookieOptions)
    } else {
      Cookies.remove(TOKEN_COOKIE_NAME)
      Cookies.remove(TOKEN_EXPIRY_DATE_COOKIE_NAME)
    }

    if (newUserInfo) {
      Cookies.set(USER_INFO_COOKIE_NAME, encryptData(newUserInfo), cookieOptions)
    } else {
      Cookies.remove(USER_INFO_COOKIE_NAME)
    }
  }

  function updateUserInfo(updatedFields) {
    if (!userInfo.value) return
    const newUserInfo = { ...userInfo.value, ...updatedFields }
    userInfo.value = newUserInfo

    const cookieOptions = {
      expires: new Date(expiresAt.value),
      secure: isSecure,
      sameSite: 'strict',
    }
    Cookies.set(USER_INFO_COOKIE_NAME, encryptData(newUserInfo), cookieOptions)
  }

  function clearAuthData() {
    token.value = null
    expiresAt.value = null
    userInfo.value = null
    Cookies.remove(TOKEN_COOKIE_NAME)
    Cookies.remove(TOKEN_EXPIRY_DATE_COOKIE_NAME)
    Cookies.remove(USER_INFO_COOKIE_NAME)
    genOptionStore.reset()
    // Admin and client accounts share browsers: never show the next user cached server data.
    queryClient.clear()
  }

  async function login(credentials, { preserveSessionOnError = false } = {}) {
    try {
      const { data } = await instance.post(authApiUrl, credentials)
      const isResponseOk = processResponse(data);
      if (!isResponseOk) {
        if (!preserveSessionOnError) clearAuthData()
        return data
      }
      const { token, expiresAt, loggedUser } = data.data
      setAuthCookies(token, expiresAt, loggedUser)
      return data
    } catch (error) {
      console.error('Login failed:', error)
      if (!preserveSessionOnError) clearAuthData()
      return error.response.data
    }
  }

  async function logout() {
    try {
    } catch (error) {
      console.error('Logout API call failed (client-side data cleared anyway):', error)
    } finally {
      clearAuthData()
      console.log('Logout successful!')
    }
  }

  const sendOTP = async (payloads) => {
    try {
      const { data } = await addRecord('', 'otp',
        { values: payloads, type: 'send_otp', config: { apiUrl: userApiURL } })
      return data
    } catch (error) {
      return error.response.data
    }
  }

  const verifyOtp = async (payloads) => {
    try {
      const { data } = await addRecord('', 'otp',
        { values: payloads, type: 'confirm_otp', config: { apiUrl: userApiURL } })
      return data
    } catch (error) {
      return error.response.data
    }

  }


  return {
    isLoggedIn,
    userInfo,
    getUserToken,
    getUserLogInfo,
    getTokenExpiresAt,
    getUserFullName,
    login,
    logout,
    updateUserInfo,
    clearAuthData,
    sendOTP,
    verifyOtp,
  }
})
